import React from 'react';
import { Dialog } from '@headlessui/react';
import { XCircle, Tag, Calendar, CheckCircle2, Rocket, Zap, Bug, ArrowUpCircle } from 'lucide-react';
import { useChangelog } from '../hooks/useChangelog';

interface ChangelogModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ChangelogModal({ isOpen, onClose }: ChangelogModalProps) {
  const { versions } = useChangelog();

  const getIcon = (version: string) => {
    const majorVersion = parseInt(version.split('.')[0]);
    switch (majorVersion) {
      case 1:
        return <Rocket className="w-6 h-6 text-blue-500" />;
      case 2:
        return <Zap className="w-6 h-6 text-purple-500" />;
      case 3:
        return <ArrowUpCircle className="w-6 h-6 text-green-500" />;
      default:
        return <Tag className="w-6 h-6 text-gray-500" />;
    }
  };

  const getChangeIcon = (change: string) => {
    if (change.toLowerCase().includes('correção') || change.toLowerCase().includes('fix')) {
      return <Bug className="w-4 h-4 text-red-500 flex-shrink-0" />;
    }
    return <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('pt-PT', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="w-full max-w-2xl rounded-xl bg-white shadow-2xl">
          <div className="relative">
            <div className="px-6 py-4 border-b border-gray-200">
              <Dialog.Title className="text-lg font-semibold text-gray-900">
                Histórico de Atualizações
              </Dialog.Title>
              <button
                onClick={onClose}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-500"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
              {versions.map((version, index) => (
                <div
                  key={version.version}
                  className={`relative pb-8 ${
                    index !== versions.length - 1 ? 'border-l-2 border-gray-200 ml-3' : ''
                  }`}
                >
                  <div className="flex items-center mb-4 -ml-3">
                    <div className="bg-white">{getIcon(version.version)}</div>
                    <div className="ml-4">
                      <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-3">
                        {version.title}
                        <span className="text-sm font-medium px-2.5 py-0.5 rounded-full bg-blue-100 text-blue-800">
                          v{version.version}
                        </span>
                      </h3>
                      <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                        <Calendar className="w-4 h-4" />
                        {formatDate(version.date)}
                      </div>
                    </div>
                  </div>

                  <div className="ml-8">
                    <ul className="space-y-3">
                      {version.changes.map((change, changeIndex) => (
                        <li key={changeIndex} className="flex items-start gap-3">
                          {getChangeIcon(change)}
                          <span className="text-gray-600">{change}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>

            <div className="px-6 py-4 bg-gray-50 rounded-b-xl border-t border-gray-200">
              <p className="text-sm text-gray-500 text-center">
                Todas as atualizações são automaticamente aplicadas à sua conta.
              </p>
            </div>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
